/**
 * Money and minutes for display, in one place, so the dashboard never shows the
 * same amount two ways. Ported from the Angular portal (`core/money.ts`,
 * `features/billing/format.ts`) so both apps word a balance identically.
 */

const dollars = (digits: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
const CENTS = dollars(2);
const SUB_CENT = dollars(4);
const TENTHS = new Intl.NumberFormat("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

// `|| 0` also turns -0 into 0, which would otherwise read "-$0.00".
const finite = (value: number) => (Number.isFinite(value) ? value : 0) || 0;

/**
 * Cents normally; an amount smaller than a cent keeps four digits, because a
 * real charge rounded to "$0.00" reads as nothing having happened. A negative
 * balance reads "-$15.39", not "$-15.39".
 */
export function formatMoney(value: number): string {
  const amount = finite(value);
  return (amount !== 0 && Math.abs(amount) < 0.01 ? SUB_CENT : CENTS).format(amount);
}

/** A wallet movement with its direction shown: "+$20.00", "−$5.00". The sign is the row's own. */
export function formatSignedMoney(value: number): string {
  const amount = finite(value);
  return `${amount < 0 ? "−" : "+"}${formatMoney(Math.abs(amount))}`;
}

/** Free minutes to a tenth: 9.8833 reads "9.9". */
export function formatMinutes(minutes: number): string {
  return TENTHS.format(Math.max(0, finite(minutes)));
}
